import { ApiKeyScope } from './types';

export const WORKSPACE_KEY_MGMT_MIN_PL = 70;
export const API_KEYS_PAGE_SIZE = 20;
export const DOCS_URL = 'https://docs.meedio.eu';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

/** Power-level dimension a scope is evaluated against (matches backend matrix). */
export type ApiKeyScopeDimension = 'workspace' | 'meeting';

export interface ApiKeyScopeDefinition {
  scope: ApiKeyScope;
  /** i18n key for the human-readable checkbox / tooltip label. */
  labelKey: string;
  /** Minimum power level the actor must hold in `dimension` to grant the scope. */
  minPowerLevel: number;
  dimension: ApiKeyScopeDimension;
}

// Array order is the canonical scope order; the create modal lays these out in a
// two-column grid with column-major fill (md:grid-flow-col) to match Figma 6392:66582.
export const SCOPES: ApiKeyScopeDefinition[] = [
  {
    scope: 'rooms:create',
    labelKey: 'workspaces_settings.api_keys.scopes.rooms_create',
    minPowerLevel: 10,
    dimension: 'workspace',
  },
  {
    scope: 'rooms:read',
    labelKey: 'workspaces_settings.api_keys.scopes.rooms_read',
    minPowerLevel: 0,
    dimension: 'workspace',
  },
  {
    scope: 'waiting-list:manage',
    labelKey: 'workspaces_settings.api_keys.scopes.waiting_list_manage',
    minPowerLevel: 25,
    dimension: 'meeting',
  },
  {
    scope: 'rooms:delete',
    labelKey: 'workspaces_settings.api_keys.scopes.rooms_delete',
    minPowerLevel: 85,
    dimension: 'meeting',
  },
  {
    scope: 'moderators:manage',
    labelKey: 'workspaces_settings.api_keys.scopes.moderators_manage',
    minPowerLevel: 90,
    dimension: 'meeting',
  },
];

export type ExpirationPreset = 'week' | 'month' | 'quarter' | 'half_year' | 'no_expiration';

/** Day offsets for each finite expiration preset (No expiration maps to `null`). */
export const EXPIRATION_PRESET_DAYS: Record<Exclude<ExpirationPreset, 'no_expiration'>, number> = {
  week: 7,
  month: 30,
  quarter: 90,
  half_year: 180,
};

/**
 * Resolves an expiration preset to an explicit `expires_at` value.
 * Returns an ISO 8601 string for finite presets, or `null` for "No expiration".
 */
export const resolveExpiresAt = (preset: ExpirationPreset, from: Date = new Date()): string | null => {
  if (preset === 'no_expiration') return null;
  return new Date(from.getTime() + EXPIRATION_PRESET_DAYS[preset] * MS_PER_DAY).toISOString();
};

/** Formats an ISO date as `MMM D, YYYY` (e.g. `Jun 22, 2026`) for the create helper text. */
export const formatExpiryDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** Builds the masked list-row display string: `{prefix} • ****-****-****-{hint}`. */
export const formatApiKeyDisplay = (prefix: string, hint: string): string => `${prefix} • ****-****-****-${hint}`;

/**
 * Formats the remaining time until `expiresAt` as `{N}d {h}h {m}min` (e.g. `27d 3h 7min`).
 * Negative remainders are clamped to zero.
 */
export const formatExpiryCountdown = (expiresAt: string, now: Date = new Date()): string => {
  const remainingMs = Math.max(0, new Date(expiresAt).getTime() - now.getTime());
  const totalMinutes = Math.floor(remainingMs / (60 * 1000));
  const days = Math.floor(totalMinutes / (MINUTES_PER_HOUR * HOURS_PER_DAY));
  const hours = Math.floor((totalMinutes % (MINUTES_PER_HOUR * HOURS_PER_DAY)) / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  return `${days}d ${hours}h ${minutes}min`;
};
